/**
 * React Forgot Password Page Component
 *
 * Matches the styling and layout of the AngularJS forgot password page
 *
 * Features:
 * - Password reset email request
 * - Exact styling match with AngularJS version
 * - Error handling and validation
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './LoginPage.css'; // Reuse login page styles

interface ForgotPasswordPageProps {
  logo?: string;
  version?: string;
  deployedEnv?: string;
}

interface ForgotPasswordError {
  field?: string;
  message: string;
}

const ForgotPasswordPage: React.FC<ForgotPasswordPageProps> = ({
  logo = '/static/imgs/tecdoc-logo.png.webp',
  version = '',
  deployedEnv = ''
}) => {
  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState<ForgotPasswordError[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrors([]);
    setIsLoading(true);

    try {
      const response = await fetch('/auth/forgot_password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'X-CSRFToken': getCsrfToken()
        },
        credentials: 'include',
        body: new URLSearchParams({
          email: email
        })
      });

      if (response.ok) {
        setIsSuccess(true);
      } else {
        const text = await response.text();
        // Try to parse error from response
        if (text.includes('error') || text.includes('invalid')) {
          setErrors([{ message: 'Email address not found. Please check and try again.' }]);
        } else {
          setErrors([{ message: 'Failed to send reset email. Please try again.' }]);
        }
      }
    } catch (error) {
      setErrors([{ message: 'An error occurred. Please try again.' }]);
      console.error('Forgot password error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getCsrfToken = (): string => {
    const cookie = document.cookie
      .split('; ')
      .find(row => row.startsWith('csrftoken='));
    return cookie ? cookie.split('=')[1] : '';
  };

  const fieldErrors = (fieldName: string) => {
    return errors.filter(e => e.field === fieldName);
  };

  const nonFieldErrors = errors.filter(e => !e.field);

  if (isSuccess) {
    return (
      <div id="main">
        <div id="values" style={{ textAlign: 'center', top: '100px' }}>
          <img src="/static/imgs/tecdoc-logo.png.webp" alt="TECDoc" />
        </div>

        <div id="auth-column">
          <div id="auth-logo-container">
            {logo && <img id="auth-logo" src={logo} alt="Facility Logo" />}
          </div>

          <div id="auth-form-container">
            <h1>PASSWORD RESET EMAIL SENT</h1>

            <div style={{ padding: '20px', textAlign: 'center' }}>
              <p style={{ marginBottom: '20px', fontSize: '16px', color: '#28a745' }}>
                ✅ Check your email for password reset instructions.
              </p>
              <p style={{ marginBottom: '30px', fontSize: '14px', color: '#666' }}>
                We've sent an email to <strong>{email}</strong> with instructions to reset your password.
              </p>
              <p style={{ marginBottom: '20px', fontSize: '14px', color: '#666' }}>
                If you don't see the email, please check your spam folder.
              </p>
              <Link 
                to="/login" 
                style={{
                  display: 'inline-block',
                  marginTop: '20px',
                  color: 'red',
                  textDecoration: 'none',
                  fontSize: '16px'
                }}
              >
                ← Back to Login
              </Link>
            </div>
          </div>

          <span className="version-number">
            {version} {deployedEnv}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div id="main">
      <div id="values" style={{ textAlign: 'center', top: '100px' }}>
        <img src="/static/imgs/tecdoc-logo.png.webp" alt="TECDoc" />
      </div>

      <div id="auth-column">
        <div id="auth-logo-container">
          {logo && <img id="auth-logo" src={logo} alt="Facility Logo" />}
        </div>

        <div id="auth-form-container">
          <h1>FORGOT PASSWORD</h1>

          <p style={{ marginBottom: '20px', fontSize: '14px', color: '#666', textAlign: 'center' }}>
            Enter your email address and we'll send you instructions to reset your password.
          </p>

          {errors.length > 0 && fieldErrors('email').length === 0 && (
            <p className="errornote">Please correct the errors below.</p>
          )}

          {nonFieldErrors.map((error, index) => (
            <p key={index} className="errornote">
              {error.message}
            </p>
          ))}

          <form onSubmit={handleSubmit} id="login-form">
            <div className="field spaced">
              <label htmlFor="id_email" className="label">Email Address</label>
              <input
                type="email"
                id="id_email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isLoading}
                required
                autoFocus
                placeholder="your.email@example.com"
              />
              {fieldErrors('email').map((error, index) => (
                <p key={index} className="errornote">{error.message}</p>
              ))}
            </div>

            <div className="submit-row">
              <input
                type="submit"
                value={isLoading ? 'SENDING...' : 'SEND RESET EMAIL'}
                className="auth-button"
                disabled={isLoading}
              />
            </div>

            <div style={{ marginTop: '20px', textAlign: 'center' }}>
              <Link 
                to="/login" 
                style={{
                  color: 'red',
                  textDecoration: 'none',
                  fontSize: '14px'
                }}
              >
                ← Back to Login
              </Link>
            </div>
          </form>
        </div>

        <span className="version-number">
          {version} {deployedEnv}
        </span>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
